<template>
  <div
    :class="['render', options.id, { 'thank-you': isThankYou }]"
    :style="{ '--active-color': options.designOptions.activeColor }"
  >
    <OverlayControls
      v-for="(attributes, name) in renderBlocks"
      :key="name"
      :show="isBuilder && overlay[name]"
      blackout
      @mouseenter.native="$set(overlay, name, true)"
      @mouseleave.native="$set(overlay, name, false)"
    >
      <component
        :is="renderComponents[name]"
        :class="{ 'render-block': isBuilder, active: isBuilder && overlay[name] }"
        v-bind="attributes"
      />
      <template #controls>
        <div class="btn-block text-right">
          <AppButton
            :label="$t('button.edit')"
            color="white"
            text-color="black"
            icon-right="fl:pen"
            icon-right-size="20px"
            icon-right-color="primary"
            padding="8px 17px"
            size="16px"
            @click="$emit('edit', { name, source: editSource })"
          />
        </div>
      </template>
    </OverlayControls>

    <img
      v-if="showPixel && pixelUrl"
      :src="pixelUrl"
      alt="pixel"
    >
  </div>
</template>

<script>
import OverlayControls from '../OverlayControls';
import AppButton from '../buttons/AppButton';
import AboutSection from './AboutSection';
import BenefitSection from './BenefitSection';
import BlockquoteSection from './BlockquoteSection';
import ColumnListSection from './ColumnListSection';
import EmbedSection from './EmbedSection';
import FAQSection from './FAQSection';
import FooterSection from './FooterSection';
import ListSection from './ListSection';
import LogoSection from './LogoSection';
import PartnersSection from './PartnersSection';
import PromoSection from './PromoSection';
import ShortHorizontalListSection from './ShortHorizontalListSection';
import TeamSection from './TeamSection';
import TextImageSection from './TextImageSection';
import ThankYouSection from './ThankYouSection';
import TimerSection from './TimerSection';
import './styles/template.scss';

export default {
  name: 'Render',
  components: {
    OverlayControls,
    AboutSection,
    AppButton,
    BenefitSection,
    BlockquoteSection,
    ColumnListSection,
    EmbedSection,
    FAQSection,
    FooterSection,
    ListSection,
    LogoSection,
    PartnersSection,
    PromoSection,
    ShortHorizontalListSection,
    TeamSection,
    TextImageSection,
    ThankYouSection,
    TimerSection,
  },
  props: {
    isThankYou: {
      type: Boolean,
      default: false,
    },
    options: {
      type: Object,
      required: true,
    },
    formGuid: {
      type: String,
      default: undefined,
    },
    isBuilder: {
      type: Boolean,
      default: false,
    },
    // used in CTAButton
    isPreview: {
      type: Boolean,
      default: false,
    },
    affiliateId: {
      type: String,
      default: undefined,
    },
    digistoreProductId: {
      type: Number,
      default: undefined,
    },
  },
  emits: ['edit'],
  data() {
    return {
      parentElement: undefined,
      sourceStyles: undefined,
      pixelUrl: undefined,
      overlay: {},
      renderComponents: {
        [ENUMS.PAGE_BLOCKS.LOGO]: 'LogoSection',
        [ENUMS.PAGE_BLOCKS.PROMO]: 'PromoSection',
        [ENUMS.PAGE_BLOCKS.PARTNERS]: 'PartnersSection',
        [ENUMS.PAGE_BLOCKS.BLOCKQUOTE]: 'BlockquoteSection',
        [ENUMS.PAGE_BLOCKS.FAQ]: 'FAQSection',
        [ENUMS.PAGE_BLOCKS.ABOUT]: 'AboutSection',
        [ENUMS.PAGE_BLOCKS.TEAM]: 'TeamSection',
        [ENUMS.PAGE_BLOCKS.EMBED]: 'EmbedSection',
        [ENUMS.PAGE_BLOCKS.FOOTER]: 'FooterSection',
        [ENUMS.PAGE_BLOCKS.THANK_YOU]: 'ThankYouSection',

        [ENUMS.PAGE_BLOCKS.TIMER]: 'TimerSection',
        [ENUMS.PAGE_BLOCKS.BENEFIT]: 'BenefitSection',
        [ENUMS.PAGE_BLOCKS.LIST]: 'ListSection',

        [ENUMS.PAGE_BLOCKS.SHORT_HORIZONTAL_LIST]: 'ShortHorizontalListSection',
        [ENUMS.PAGE_BLOCKS.COLUMN_LIST]: 'ColumnListSection',
        [ENUMS.PAGE_BLOCKS.TEXT_IMAGE]: 'TextImageSection',
      },
    };
  },
  computed: {
    showPixel() {
      return this.isThankYou
        && this.affiliateId
        && !this.isPreview
        && !this.isBuilder;
    },
    editSource() {
      return this.isThankYou ? 'thankYouBlocks' : 'blocks';
    },
    renderBlocks() {
      const key = this.isThankYou ? 'thankYouBlocks' : 'blocks';
      const blocks = this.options[key] || [];
      return blocks
        .filter((item) => item.isActive)
        .reduce((result, block) => {
          result[block.id] = { ...block };
          return result;
        }, {});
    },
  },
  watch: {
    'options.designOptions.useBackgroundImage': {
      handler() {
        this.updateBackground();
      },
    },
    'options.designOptions.backgroundImageUrl': {
      handler() {
        this.updateBackground();
      },
    },
  },
  mounted() {
    this.$root.$on('update-email', this.setPixel);
    this.setFavicon();
    this.updateBackground();
  },
  beforeDestroy() {
    this.$root.$off('update-email', this.setPixel);
    this.parentElement.style = this.sourceStyles;
  },
  methods: {
    async setPixel() {
      const email = this.$q.localStorage.getItem('wbmLeadEmail');
      if (!this.showPixel || !email) {
        return;
      }
      try {
        const textAsBuffer = new TextEncoder().encode(email);
        const hashBuffer = await window.crypto.subtle.digest('SHA-256', textAsBuffer);
        const hashArray = [...new Uint8Array(hashBuffer)];
        const digest = hashArray.map((hash) => hash.toString(16).padStart(2, '0')).join('');
        const response = await fetch(`https://www.digistore24.com/buy/promolink/get_onetime_pass/${digest}`);
        const { password } = await response.json();
        setTimeout(() => {
          this.pixelUrl = `https://www.digistore24.com/buy/promolink/email/${this.affiliateId.toLowerCase()}/CAMPAIGNKEY/${this.digistoreProductId}/${digest}/${password}.png?group_sids=&ds24tr=`;
        }, 500);
      } catch (error) {
        console.error(error);
      }
    },
    updateBackground() {
      this.parentElement = this.isBuilder
        ? document.querySelectorAll('.render')[0]
        : document.querySelectorAll('body')[0];

      this.sourceStyles = this.parentElement.style;

      if (this.options.designOptions.pageBackground) {
        this.parentElement.style.backgroundColor = this.options.designOptions.pageBackground;
      }

      if (this.parentElement && this.options.designOptions.useBackgroundImage) {
        if (this.options.designOptions.backgroundImageUrl) {
          this.parentElement.style.backgroundImage = `url(${this.options.designOptions.backgroundImageUrl})`;
          this.parentElement.style.backgroundRepeat = 'no-repeat';
          this.parentElement.style.backgroundSize = 'cover';
        }
      } else {
        this.parentElement.style.backgroundImage = 'none';
      }
    },
    setFavicon() {
      if (!this.options.designOptions.favicon || this.isBuilder) {
        return;
      }

      const links = document.querySelectorAll("link[rel~='icon']");
      if (links.length > 0) {
        links.forEach((link) => {
          link.href = this.options.designOptions.favicon;
        });
      } else {
        const link = document.createElement('link');
        link.rel = 'icon';
        link.href = this.options.designOptions.favicon;
        document.querySelectorAll('head')[0].append(link);
      }
    },
  },
};
</script>

<style scoped lang="scss">
.render-block {
  transition: all 0.5s ease;
  border-bottom: 1px dashed transparent;
  border-top: 1px dashed transparent;
  &.active {
    border-bottom-color: $primary;
    border-top-color: $primary;
  }
}

.btn-block {
  position: sticky;
  top: 16px;
  right: 16px;
}

::v-deep .blackout {
  background: rgba(0, 0, 0, 0.35);
}
</style>
